import React, { useState, useEffect, useCallback } from 'react';
import {
  Download, Plus, AlertTriangle, CheckCircle, XCircle,
  Zap, Users, CreditCard, Calendar, ShieldOff,
  Loader2, RefreshCw, Eye, UserPlus,
} from 'lucide-react';
import PageHeader from '../PageHeader';

// ─── helpers ──────────────────────────────────────────────────────────────────
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}
function fmt(n) { return Number(n).toLocaleString('en-IN'); }
function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Plan configs ─────────────────────────────────────────────────────────────
const PLAN_CONFIGS = {
  starter: {
    id: 'starter', name: 'Starter', price: 999, tagline: 'Perfect for small teams getting started',
    maxAiAgents: 3, maxHumanAgents: 3, maxUsers: 3, creditsTotal: 5000,
    features: [
      { label: '3 AI agents', included: true },
      { label: '3 Human agents', included: true },
      { label: '3 Users limit', included: true },
      { label: '5,000 credits/mo', included: true },
      { label: 'WhatsApp + web', included: true },
      { label: 'Email support', included: true },
      { label: 'RBAC', included: false },
    ],
  },
  growth: {
    id: 'growth', name: 'Growth', price: 2499, tagline: 'For growing businesses and teams',
    maxAiAgents: 5, maxHumanAgents: 5, maxUsers: 5, creditsTotal: 25000,
    features: [
      { label: '5 AI agents', included: true },
      { label: '5 Human agents', included: true },
      { label: '5 Users limit', included: true },
      { label: '25,000 credits/mo', included: true },
      { label: 'WhatsApp + web', included: true },
      { label: 'Email support', included: true },
      { label: 'Priority support', included: true },
    ],
  },
  professional: {
    id: 'professional', name: 'Professional', price: 4999, tagline: 'For power users and scaling teams',
    maxAiAgents: 7, maxHumanAgents: 7, maxUsers: 7, creditsTotal: 75000,
    features: [
      { label: '7 AI agents', included: true },
      { label: '7 Human agents', included: true },
      { label: '7 Users limit', included: true },
      { label: '75,000 credits/mo', included: true },
      { label: 'All channels', included: true },
      { label: 'Priority support', included: true },
      { label: 'Basic analytics', included: true },
    ],
  },
  enterprise: {
    id: 'enterprise', name: 'Enterprise', price: 'Custom', tagline: 'For large enterprises and organizations',
    maxAiAgents: 999, maxHumanAgents: 999, maxUsers: 999, creditsTotal: 999999,
    features: [
      { label: 'Unlimited AI agents', included: true },
      { label: 'Unlimited Human agents', included: true },
      { label: 'Unlimited Users limit', included: true },
      { label: 'Custom credits', included: true },
      { label: 'RBAC', included: true },
      { label: 'SLA + dedicated CSM', included: true },
      { label: 'Custom contract', included: true },
    ],
  },
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── sub-components ───────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color = 'text-[#82bbe0]', highlight, loading }) {
  const bgLightColor = color.includes('text-[#82bbe0]')
    ? 'bg-[#82bbe0]/10'
    : color.includes('text-purple-500')
      ? 'bg-purple-50'
      : color.includes('text-amber-500')
        ? 'bg-amber-50'
        : 'bg-gray-50';

  return (
    <div className={`bg-white border rounded-[24px] p-5 flex items-start gap-4 transition-all duration-300 hover:shadow-lg hover:shadow-gray-100/80
      ${highlight ? 'border-amber-300 bg-amber-50/50' : 'border-gray-100 shadow-md shadow-gray-100/30'}`}
    >
      <div className={`p-3 rounded-2xl flex-shrink-0 flex items-center justify-center ${bgLightColor} ${color}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{label}</p>
        {loading
          ? <div className="h-5 w-20 bg-gray-100 animate-pulse rounded mt-1.5" />
          : <p className="text-lg font-black text-gray-900 leading-snug truncate mt-0.5">{value}</p>
        }
        {sub && <p className="text-[10px] text-gray-400 mt-1 font-medium">{sub}</p>}
      </div>
    </div>
  );
}

function ProgressBar({ used, total, color = 'bg-[#82bbe0]' }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div
        className={`h-2 rounded-full transition-all duration-500 ${pct >= 85 ? 'bg-red-400' : color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ─── User Slot Row — view only (no edit/delete) ───────────────────────────────
function UserSlotRow({ slot }) {
  const roleColor = {
    Admin: 'bg-purple-100 text-purple-700',
    Manager: 'bg-blue-100 text-blue-700',
    Agent: 'bg-green-100 text-green-700',
    Viewer: 'bg-gray-100 text-gray-500',
  }[slot.role] || 'bg-gray-100 text-gray-500';

  return (
    <div className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100">
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#82bbe0]/15 text-[#82bbe0] font-bold text-sm">
        {slot.name ? slot.name.charAt(0).toUpperCase() : '?'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{slot.name || '—'}</p>
        <p className="text-xs text-gray-400 truncate">{slot.email || '—'}</p>
      </div>
      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full flex-shrink-0 ${roleColor}`}>
        {slot.role}
      </span>
    </div>
  );
}

// ─── Add User Slot Modal ──────────────────────────────────────────────────────
function AddUserSlotModal({ onAdd, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'Agent' });
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    await onAdd(form);
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-[32px] shadow-2xl border border-gray-100 w-full max-w-sm mx-4 p-8 space-y-6 animate-scaleIn">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Add User Slot</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-all font-bold">
            ✕
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Full Name</label>
            <input
              autoFocus
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Ravi Kumar"
              className="w-full text-xs font-semibold border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:border-[#82bbe0] focus:ring-2 focus:ring-[#82bbe0]/20 transition-all"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="e.g. ravi@company.com"
              className="w-full text-xs font-semibold border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:border-[#82bbe0] focus:ring-2 focus:ring-[#82bbe0]/20 transition-all"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 block">Role</label>
            <select
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full text-xs font-semibold border border-gray-200 rounded-xl px-3.5 py-3 focus:outline-none focus:border-[#82bbe0] focus:ring-2 focus:ring-[#82bbe0]/20 transition-all bg-white"
            >
              {['Admin', 'Manager', 'Agent', 'Viewer'].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSubmit}
            disabled={saving || !form.name.trim() || !form.email.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-black uppercase tracking-wider text-white bg-[#82bbe0] rounded-xl hover:bg-[#6aabce] transition-all disabled:opacity-60 shadow-lg shadow-[#82bbe0]/25 active:scale-[0.98]"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
            Add User
          </button>
          <button
            onClick={onClose}
            className="px-5 py-3 text-xs font-bold text-gray-500 bg-gray-50 border border-gray-100 rounded-xl hover:bg-gray-100 transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Agent Slot — view only (no edit/delete) ──────────────────────────────────
function AgentSlot({ agent, index, onAdd, disabled }) {
  if (!agent) {
    return (
      <button
        disabled={disabled}
        onClick={onAdd}
        className="flex items-center gap-3 py-3 px-4 border border-dashed border-gray-200 rounded-xl text-gray-400
          hover:border-[#82bbe0] hover:text-[#82bbe0] cursor-pointer transition-all group w-full disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus size={16} className="group-hover:scale-110 transition-transform" />
        <span className="text-sm">Empty slot — create an agent</span>
      </button>
    );
  }

  const statusColor = {
    Active: 'bg-green-100 text-green-700',
    Inactive: 'bg-gray-100 text-gray-500',
    Email: 'bg-blue-100 text-blue-700',
    WhatsApp: 'bg-emerald-100 text-emerald-700',
    Web: 'bg-violet-100 text-violet-700',
  }[agent.status] || 'bg-gray-100 text-gray-500';

  return (
    <div className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-xl border border-gray-100">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#82bbe0]/10 text-[#82bbe0]">
        <Zap size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{agent.name}</p>
        <p className="text-xs text-gray-400">{agent.convToday ?? 0} conv today</p>
      </div>
      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusColor}`}>
        {agent.status}
      </span>
    </div>
  );
}

// ─── Plan Confirm Modal ──────────────────────────────────────────────────────
const ADDON_PRICE_PER_AGENT = 900;

function PlanConfirmModal({ plan, billingCycle, onConfirm, onClose, loading }) {
  const isEnterprise = plan.price === 'Custom';
  const basePrice = isEnterprise ? 0 : (billingCycle === 'yearly' ? Math.round(plan.price * 0.8) * 12 : plan.price);
  const [extraAiAgents, setExtraAiAgents] = useState(0);
  const [extraHumanAgents, setExtraHumanAgents] = useState(0);
  const [extraCredits, setExtraCredits] = useState(null); // pack object or null

  const TOPUP_PACKS = [
    { k: '25K', price: 499 },
    { k: '100K', price: 1499 },
    { k: '500K', price: 3999 },
  ];

  const addonCost = (extraAiAgents + extraHumanAgents) * ADDON_PRICE_PER_AGENT + (extraCredits?.price ?? 0);
  const subtotal = basePrice + addonCost;
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-[32px] shadow-2xl border border-gray-100 w-full max-w-lg mx-4 p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">{plan.name} Plan</h3>
            <p className="text-xs text-gray-400 mt-0.5">{plan.tagline}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-all font-bold">✕</button>
        </div>

        {/* Plan features */}
        <div className="bg-gray-50 rounded-2xl p-4">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">What's included</p>
          <ul className="space-y-2">
            {plan.features.map((f, i) => (
              <li key={i} className={`flex items-center gap-2 text-xs ${f.included ? 'text-gray-700' : 'text-gray-300'}`}>
                {f.included
                  ? <CheckCircle size={13} className="text-[#82bbe0] flex-shrink-0" />
                  : <XCircle size={13} className="text-gray-200 flex-shrink-0" />}
                <span className="font-medium">{f.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Add-ons */}
        <div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Add-ons <span className="normal-case font-medium text-gray-400">(₹{fmt(ADDON_PRICE_PER_AGENT)}/agent)</span></p>
          <div className="space-y-3">
            {/* Extra AI Agents */}
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
              <div>
                <p className="text-xs font-bold text-gray-800">Extra AI Agents</p>
                <p className="text-[10px] text-gray-400">₹{fmt(ADDON_PRICE_PER_AGENT)} each</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setExtraAiAgents(n => Math.max(0, n - 1))} className="w-7 h-7 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-black text-sm flex items-center justify-center transition-all">−</button>
                <span className="text-sm font-black text-gray-900 w-5 text-center">{extraAiAgents}</span>
                <button onClick={() => setExtraAiAgents(n => n + 1)} className="w-7 h-7 rounded-lg bg-[#82bbe0]/20 hover:bg-[#82bbe0]/30 text-[#82bbe0] font-black text-sm flex items-center justify-center transition-all">+</button>
              </div>
            </div>
            {/* Extra Human Agents */}
            <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
              <div>
                <p className="text-xs font-bold text-gray-800">Extra Human Agents</p>
                <p className="text-[10px] text-gray-400">₹{fmt(ADDON_PRICE_PER_AGENT)} each</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setExtraHumanAgents(n => Math.max(0, n - 1))} className="w-7 h-7 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-black text-sm flex items-center justify-center transition-all">−</button>
                <span className="text-sm font-black text-gray-900 w-5 text-center">{extraHumanAgents}</span>
                <button onClick={() => setExtraHumanAgents(n => n + 1)} className="w-7 h-7 rounded-lg bg-[#82bbe0]/20 hover:bg-[#82bbe0]/30 text-[#82bbe0] font-black text-sm flex items-center justify-center transition-all">+</button>
              </div>
            </div>
            {/* Credit top-up */}
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-xs font-bold text-gray-800 mb-2">Credit Top-up Pack</p>
              <div className="flex gap-2">
                {TOPUP_PACKS.map(pack => (
                  <button
                    key={pack.k}
                    onClick={() => setExtraCredits(extraCredits?.k === pack.k ? null : pack)}
                    className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${extraCredits?.k === pack.k
                      ? 'border-[#82bbe0] bg-[#82bbe0]/10 text-[#82bbe0]'
                      : 'border-gray-200 text-gray-500 hover:border-[#82bbe0]/40'
                      }`}
                  >
                    <div>{pack.k}</div>
                    <div className="text-[10px] font-medium text-gray-400">₹{fmt(pack.price)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Price breakdown */}
        <div className="border-t border-gray-100 pt-4 space-y-2">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Base plan ({billingCycle})</span>
            <span className="font-semibold text-gray-800">₹{fmt(basePrice)}</span>
          </div>
          {addonCost > 0 && (
            <div className="flex justify-between text-xs text-gray-500">
              <span>Add-ons</span>
              <span className="font-semibold text-gray-800">₹{fmt(addonCost)}</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-gray-500">
            <span>GST (18%)</span>
            <span className="font-semibold text-gray-800">₹{fmt(gst)}</span>
          </div>
          <div className="flex justify-between text-sm font-black text-gray-900 pt-2 border-t border-gray-100">
            <span>Total</span>
            <span>₹{fmt(total)}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => onConfirm({ extraAiAgents, extraHumanAgents, extraCredits, total })}
            disabled={loading}
            className="flex-1 py-3.5 rounded-2xl bg-[#82bbe0] text-white text-xs font-black uppercase tracking-wider hover:bg-[#6aabce] transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-[#82bbe0]/25 active:scale-[0.98]"
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : <><CreditCard size={14} /> Proceed to Pay</>}
          </button>
          <button onClick={onClose} className="px-5 py-3 text-xs font-bold text-gray-500 bg-gray-50 border border-gray-100 rounded-2xl hover:bg-gray-100 transition-all active:scale-[0.98]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Plan Card ──────────────────────────────────────────────────────────────
function PlanCard({ plan, isCurrent, billingCycle, onOpenModal, loading }) {
  const monthlyDiscountedPrice =
    billingCycle === 'yearly' && plan.price !== 'Custom'
      ? Math.round(plan.price * 0.8)
      : plan.price;
  const isEnterprise = plan.price === 'Custom';

  return (
    <div className={`relative rounded-[28px] border-2 p-6 flex flex-col gap-4 transition-all duration-300 ${isCurrent
      ? 'border-[#82bbe0] bg-[#82bbe0]/5 shadow-xl shadow-[#82bbe0]/10 scale-[1.02]'
      : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-lg hover:shadow-gray-100/50'
      }`}>
      {isCurrent && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="bg-[#82bbe0] text-white text-[10px] font-black uppercase tracking-wider px-3.5 py-1 rounded-full shadow-md whitespace-nowrap">
            Current plan
          </span>
        </div>
      )}
      <div>
        <h4 className="text-sm font-black text-gray-900 uppercase tracking-wider">{plan.name}</h4>
        {billingCycle === 'yearly' && !isEnterprise ? (
          <div className="mt-2">
            <p className="text-2xl font-black text-gray-900 leading-tight">
              ₹{fmt(monthlyDiscountedPrice)}
              <span className="text-xs font-normal text-gray-400 lowercase">/mo</span>
            </p>
            <p className="text-[10px] text-green-600 font-bold uppercase tracking-wide mt-1">
              Billed ₹{fmt(monthlyDiscountedPrice * 12)}/yr · Save 20%
            </p>
          </div>
        ) : (
          <p className="text-2xl font-black text-gray-900 mt-2">
            {isEnterprise ? 'Custom' : `₹${fmt(plan.price)}`}
            {!isEnterprise && <span className="text-xs font-normal text-gray-400 lowercase">/mo</span>}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1 font-medium leading-relaxed">{plan.tagline}</p>
      </div>
      <div className="border-t border-gray-50 my-1"></div>
      <ul className="space-y-2.5 text-xs text-gray-600 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className={`flex items-center gap-2 ${f.included ? '' : 'opacity-40'}`}>
            {f.included
              ? <CheckCircle size={14} className="text-[#82bbe0] flex-shrink-0" />
              : <XCircle size={14} className="text-gray-300 flex-shrink-0" />}
            <span className="font-medium">{f.label}</span>
          </li>
        ))}
      </ul>
      {isCurrent ? (
        !isEnterprise && (
          <>
            <div className="border-t border-gray-50 my-1"></div>
            <button
              onClick={() => onOpenModal(plan)}
              className="w-full py-3.5 rounded-2xl bg-[#82bbe0] text-white text-xs font-black uppercase tracking-wider hover:bg-[#6aabce] transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#82bbe0]/25 active:scale-[0.98]"
            >
              {loading ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : 'Renew Plan'}
            </button>
          </>
        )
      ) : (
        <>
          <div className="border-t border-gray-50 my-1"></div>
          {isEnterprise ? (
            <button
              onClick={() => window.open('mailto:contact@kambaa.in', '_blank')}
              className="w-full py-3.5 rounded-2xl border border-gray-200 text-gray-600 text-xs font-black uppercase tracking-wider hover:border-[#82bbe0] hover:text-[#82bbe0] transition-all bg-white shadow-sm hover:shadow active:scale-[0.98]"
            >
              Contact sales ↗
            </button>
          ) : (
            <button
              disabled={loading}
              onClick={() => onOpenModal(plan)}
              className="w-full py-3.5 rounded-2xl bg-gray-50 hover:bg-[#82bbe0] hover:text-white text-gray-600 hover:text-white text-xs font-black uppercase tracking-wider transition-all disabled:opacity-60 flex items-center justify-center gap-2 border border-gray-100 hover:border-transparent active:scale-[0.98]"
            >
              {loading ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : 'Switch Plan'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BillingDashboard({
  userName, subscription, onSubscriptionChange,
  onSubscriptionPay, onCreditPay, loadingId = '',
  paymentHistory = [], serverUrl,
}) {
  const envApiUrl = process.env.REACT_APP_API_BASE_URL || '';
  const SERVER_URL = serverUrl || (envApiUrl ? envApiUrl.replace('/api', '') : '');

  const [billingCycle, setBillingCycle] = useState('monthly');
  const [invoiceDropOpen, setInvoiceDropOpen] = useState(false);
  const [serviceActive, setServiceActive] = useState(true);
  const [paymentDueWarning, setPaymentDueWarning] = useState(null);
  const [autoTopup, setAutoTopup] = useState(true);
  const [agents, setAgents] = useState([]);
  const [confirmPlan, setConfirmPlan] = useState(null); // plan object to show in modal

  // ── Invoice filter state ─────────────────────────────────────────────────────
  const [invoiceFilterYear, setInvoiceFilterYear] = useState('');
  const [invoiceFilterMonth, setInvoiceFilterMonth] = useState('');

  // ── User slot state ──────────────────────────────────────────────────────────
  const [userSlots, setUserSlots] = useState([]);
  const [userSlotsLoading, setUserSlotsLoading] = useState(false);
  const [userSlotsError, setUserSlotsError] = useState(null);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);

  const TOPUP_PACKS = [
    { k: '25K', price: 499 },
    { k: '100K', price: 1499, popular: true },
    { k: '500K', price: 3999 },
  ];
  const [selectedPack, setSelectedPack] = useState(TOPUP_PACKS[0]);

  // Resolve plan config
  const resolvePlanConfig = (sub) => {
    if (!sub) return null;
    const byId = PLAN_CONFIGS[sub.planId];
    if (byId) return byId;
    const byName = PLAN_CONFIGS[sub.planName?.toLowerCase()];
    if (byName) return byName;
    for (const key of Object.keys(PLAN_CONFIGS))
      if (sub.planName?.toLowerCase().includes(key)) return PLAN_CONFIGS[key];
    return null;
  };

  const planConfig = resolvePlanConfig(subscription);

  // ── Load agents ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userName) return;
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const tenantId = userObj?.tenant_id || userName;
    fetch(`${SERVER_URL}/api/agents/${tenantId}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data.length > 0) {
          setAgents(d.data.map(a => ({
            name: a.agent_name,
            status: a.status,
            convToday: a.convs_today,
            id: a.id,
          })));
        } else if (planConfig) {
          setAgents(Array(planConfig.maxAiAgents ?? 0).fill(null));
        }
      })
      .catch(() => {
        if (planConfig) setAgents(Array(planConfig.maxAiAgents ?? 0).fill(null));
      });
  }, [userName, subscription, planConfig, SERVER_URL]);

  // ── Load user slots ──────────────────────────────────────────────────────────
  const fetchUserSlots = useCallback(async () => {
    if (!userName) return;
    setUserSlotsLoading(true);
    setUserSlotsError(null);
    try {
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const tenantId = userObj?.tenant_id || userName;
      const res = await fetch(`${SERVER_URL}/api/user-slots/${tenantId}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to load user slots');
      setUserSlots(data.data.map(s => ({
        id: s.id,
        name: s.name,
        email: s.email,
        role: s.role,
      })));
    } catch (err) {
      setUserSlotsError(err.message || 'Could not load user slots');
    } finally {
      setUserSlotsLoading(false);
    }
  }, [userName, SERVER_URL]);

  useEffect(() => { fetchUserSlots(); }, [fetchUserSlots]);

  // ── Add user slot ────────────────────────────────────────────────────────────
  async function handleAddUserSlot(form) {
    try {
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const tenantId = userObj?.tenant_id || userName;
      const res = await fetch(`${SERVER_URL}/api/user-slots/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, ...form }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Add failed');
      await fetchUserSlots();
    } catch (err) {
      console.error('Add user slot error:', err);
      setUserSlotsError(err.message);
    }
  }

  // Close invoice dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (!e.target.closest('[data-invoice-wrap]')) setInvoiceDropOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Payment due enforcement
  useEffect(() => {
    if (!subscription?.renewsAt) { setPaymentDueWarning(null); setServiceActive(true); return; }
    const days = daysUntil(subscription.renewsAt);
    if (days !== null && days <= 10) {
      setPaymentDueWarning({ days, date: subscription.renewsAt });
      setServiceActive(days > 0);
    } else {
      setPaymentDueWarning(null);
      setServiceActive(true);
    }
  }, [subscription]);

  // Derived values
  const maxAgents = subscription?.maxAiAgents ?? planConfig?.maxAiAgents ?? subscription?.maxAgents ?? planConfig?.maxAgents ?? 0;
  const activeAgentCount = agents.filter(Boolean).length;
  const creditsTotal = subscription?.creditsTotal ?? planConfig?.creditsTotal ?? 0;
  const creditsUsed = subscription?.creditsUsed ?? 0;
  const topupCredits = subscription?.topupCredits ?? 0;
  const creditsRemaining = creditsTotal - creditsUsed;
  const renewDays = daysUntil(subscription?.renewsAt);
  const plansList = Object.values(PLAN_CONFIGS);
  const currentPlanIdx = planConfig ? plansList.findIndex(p => p.id === planConfig.id) : -1;

  // User slot derived
  const maxUserSlots = subscription?.maxHumanAgents ?? planConfig?.maxHumanAgents ?? subscription?.maxUserSlots ?? planConfig?.maxAgents ?? 0;
  const usedUserSlots = userSlots.length;
  const freeUserSlots = maxUserSlots ? Math.max(0, maxUserSlots - usedUserSlots) : null;

  // ── Invoice filter derived ───────────────────────────────────────────────────
  // Collect unique years from payment history
  const invoiceYears = [...new Set(
    paymentHistory.map(r => new Date(r.paidAt).getFullYear())
  )].sort((a, b) => b - a);

  // Collect months available for selected year (or all if no year selected)
  const invoiceMonths = [...new Set(
    paymentHistory
      .filter(r => !invoiceFilterYear || new Date(r.paidAt).getFullYear() === Number(invoiceFilterYear))
      .map(r => new Date(r.paidAt).getMonth()) // 0-indexed
  )].sort((a, b) => a - b);

  const filteredInvoices = paymentHistory.filter(r => {
    const d = new Date(r.paidAt);
    const yearMatch = !invoiceFilterYear || d.getFullYear() === Number(invoiceFilterYear);
    const monthMatch = !invoiceFilterMonth || d.getMonth() === Number(invoiceFilterMonth);
    return yearMatch && monthMatch;
  });

  // ── Add agent ────────────────────────────────────────────────────────────────
  async function handleAddAgent() {
    try {
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const tenantId = userObj?.tenant_id || userName;
      const res = await fetch(`${SERVER_URL}/api/agents/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, agentName: 'New Agent', channel: 'Active', convToday: 0 }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.detail);
      const agentsRes = await fetch(`${SERVER_URL}/api/agents/${tenantId}`);
      const agentsData = await agentsRes.json();
      if (agentsData.success) {
        setAgents(agentsData.data.map(a => ({
          name: a.agent_name, status: a.status, convToday: a.convs_today, id: a.id,
        })));
      }
    } catch (err) { console.error('Add agent error:', err); }
  }

  // ── Receipt helpers ──────────────────────────────────────────────────────────
  function buildReceiptHtml(record) {
    const date = new Date(record.paidAt).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });
    const origin = window.location.origin;
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Payment Receipt – ${record.paymentId}</title>
<style>
  body{font-family:system-ui,sans-serif;background:#f9fafb;margin:0;padding:40px;color:#111}
  .card{background:#fff;max-width:480px;margin:0 auto;border-radius:16px;padding:40px;box-shadow:0 4px 24px rgba(0,0,0,.08)}
  .logo-wrap{margin-bottom:16px}
  .logo-wrap img{height:60px;object-fit:contain}
  .badge{display:inline-block;background:#e8f5ed;color:#16a34a;font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;margin-bottom:24px}
  .row{display:flex;justify-content:space-between;font-size:13px;margin-bottom:10px}
  .row .label{color:#6b7280}.row .value{font-weight:600}
  .mono{font-family:monospace;font-size:11px;color:#82bbe0}
  hr{border:none;border-top:1px solid #f0f0f0;margin:20px 0}
  .total{font-size:15px;font-weight:800}
  .footer{margin-top:28px;font-size:11px;color:#9ca3af;text-align:center}
  .footer img{height:24px;object-fit:contain;margin-bottom:6px}
</style></head><body>
<div class="card">
  <div class="logo-wrap"><img src="${origin}/logo.png" alt="Logo" /></div>
  <span class="badge">✓ Payment Confirmed</span>
  <h2 style="font-size:20px;margin:0 0 6px">Payment Receipt</h2>
  <p style="color:#6b7280;font-size:13px;margin-bottom:28px">Thank you for your purchase.</p>
  <hr/>
  <div class="row"><span class="label">Customer</span><span class="value">${userName}</span></div>
  <div class="row"><span class="label">Date</span><span class="value">${date}</span></div>
  <div class="row"><span class="label">Payment ID</span><span class="mono">${record.paymentId}</span></div>
  <div class="row"><span class="label">Plan / Pack</span><span class="value">${record.planName}</span></div>
  ${record.validUntil ? `<div class="row"><span class="label">Valid Until</span><span class="value">${new Date(record.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>` : ''}
  <hr/>
  <div class="row total"><span>Total Paid</span><span>₹${Number(record.amount).toLocaleString('en-IN')}</span></div>
  <div class="row"><span class="label">Status</span><span style="color:#16a34a;font-weight:700">Confirmed</span></div>
  <hr/>
  <div class="footer">Secured by Razorpay · 256-bit SSL encryption<br/>© ${new Date().getFullYear()} All rights reserved.</div>
</div></body></html>`;
  }

  function previewReceipt(record) {
    const url = URL.createObjectURL(new Blob([buildReceiptHtml(record)], { type: 'text/html' }));
    window.open(url, '_blank');
  }

  function downloadReceipt(record) {
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([buildReceiptHtml(record)], { type: 'text/html' })),
      download: `easychat-receipt-${record.paymentId}.html`,
    });
    a.click();
  }

  // ── Loading state ───────────────────────────────────────────────────────────
  if (subscription === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full gap-3">
        <Loader2 size={32} className="animate-spin text-[#82bbe0]" />
        <p className="text-xs text-gray-400 font-medium animate-pulse">Loading billing details...</p>
      </div>
    );
  }

  // ── No subscription ──────────────────────────────────────────────────────────
  if (!subscription.planId || subscription.planName === 'No Plan') {
    return (
      <div className="bg-transparent w-full space-y-12">
        <PageHeader
          title="Billing & Subscription"
          description="Choose a subscription plan to supercharge your AI agents, assign teammates, and gain credits."
        />
        <div className="bg-white rounded-[32px] p-8 md:p-12 border border-gray-100 shadow-xl shadow-gray-100/50">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Subscription Plan</h3>
              <p className="text-[11px] font-medium text-gray-400">Select a plan to pay via Razorpay</p>
            </div>
            <div className="flex items-center gap-1 bg-gray-50 rounded-2xl p-1 border border-gray-100">
              {['monthly', 'yearly'].map(c => (
                <button
                  key={c}
                  onClick={() => setBillingCycle(c)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${billingCycle === c ? 'bg-white shadow-md shadow-gray-100/50 text-gray-900 border border-gray-100' : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  {c === 'monthly' ? 'Monthly' : (
                    <span className="flex items-center gap-1">Yearly <span className="text-green-600 text-[10px] font-bold">-20%</span></span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {Object.values(PLAN_CONFIGS).map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={false}
                billingCycle={billingCycle}
                onOpenModal={setConfirmPlan}
                loading={loadingId === 'plan_' + plan.id}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Full dashboard ───────────────────────────────────────────────────────────
  return (
    <div className="bg-transparent w-full">
      {showAddSlotModal && (
        <AddUserSlotModal
          onAdd={handleAddUserSlot}
          onClose={() => setShowAddSlotModal(false)}
        />
      )}
      {confirmPlan && (
        <PlanConfirmModal
          plan={confirmPlan}
          billingCycle={billingCycle}
          loading={!!loadingId}
          onClose={() => setConfirmPlan(null)}
          onConfirm={({ extraAiAgents, extraHumanAgents, extraCredits, total }) => {
            setConfirmPlan(null);
            onSubscriptionPay(
              confirmPlan.id,
              confirmPlan.name,
              typeof confirmPlan.price === 'number' ? confirmPlan.price : 0,
              billingCycle,
              { extraAiAgents, extraHumanAgents, extraCredits, total }
            );
          }}
        />
      )}

      <div className="w-full space-y-12">

        {/* Service suspended banner (Commented Out)
        {!serviceActive && (
          <div className="flex items-start gap-3 bg-red-50 border-2 border-red-300 rounded-2xl px-5 py-4">
            <ShieldOff size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">  
              <p className="text-sm font-bold text-red-700">Service Suspended</p>
              <p className="text-xs text-red-600 mt-0.5">
                Your subscription expired on <strong>{formatDate(subscription.renewsAt)}</strong>.
                Renew now to restore access to agents, credits, and all features.
              </p>
              <button
                disabled={!!loadingId}
                onClick={() => setConfirmPlan(planConfig || PLAN_CONFIGS['professional'])}
                className="mt-3 text-xs font-semibold text-white bg-red-500 hover:bg-red-600 px-4 py-1.5 rounded-lg transition-all flex items-center gap-1 disabled:opacity-60"
              >
                <RefreshCw size={12} /> Renew Now
              </button>
            </div>
          </div>
        )}
        */}

        {/* Payment due warning (Commented Out)
        {paymentDueWarning && serviceActive && (
          <div className="flex items-start gap-3 bg-amber-50 border-2 border-amber-300 rounded-2xl px-5 py-4">
            <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold text-amber-800">
                Payment due in {paymentDueWarning.days} day{paymentDueWarning.days !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Your subscription renews on <strong>{formatDate(paymentDueWarning.date)}</strong>.
                {paymentDueWarning.days <= 3 && (
                  <span className="font-bold text-red-600"> Service will stop if not renewed in time.</span>
                )}
              </p>
            </div>
            <button
              disabled={!!loadingId}
              onClick={() => setConfirmPlan(planConfig || PLAN_CONFIGS['professional'])}
              className="text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg transition-all flex-shrink-0 flex items-center gap-1 disabled:opacity-60"
            >
              <CreditCard size={12} />
              Pay Now
            </button>
          </div>
        )}
        */}

        {/* Header + invoice dropdown (Commented Out)
        <PageHeader
          title="Billing & Subscription"
          description="Manage your subscription plans, credits, human agent slots, and check invoice receipts."
          actions={
            <div className="relative" data-invoice-wrap>
              <button
                onClick={() => setInvoiceDropOpen(o => !o)}
                className="flex items-center gap-2 px-5 py-3 border border-gray-200 rounded-[24px] text-sm font-bold text-gray-600 hover:border-[#82bbe0] hover:text-[#82bbe0] transition-all bg-white shadow-xl shadow-gray-100/50 hover:bg-gray-50 active:scale-[0.98]"
              >
                <Download size={15} />
                <span>Invoice History</span>
              </button>

              {invoiceDropOpen && (
                <div className="absolute right-1/2 translate-x-1/2 md:right-0 md:translate-x-0 top-full mt-3 z-50 bg-white border border-gray-100 rounded-[32px] shadow-2xl overflow-hidden w-96 text-left">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <span className="text-xs font-black text-gray-700 uppercase tracking-wider">Invoices</span>
                    <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                      {filteredInvoices.length} of {paymentHistory.length}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                    <select
                      value={invoiceFilterYear}
                      onChange={e => {
                        setInvoiceFilterYear(e.target.value);
                        setInvoiceFilterMonth(''); // reset month when year changes
                      }}
                      className="flex-1 text-xs border border-gray-200 rounded-xl px-2.5 py-2 bg-white focus:outline-none focus:border-[#82bbe0] text-gray-700 font-bold"
                    >
                      <option value="">All years</option>
                      {invoiceYears.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>

                    <select
                      value={invoiceFilterMonth}
                      onChange={e => setInvoiceFilterMonth(e.target.value)}
                      disabled={!invoiceFilterYear && invoiceMonths.length === 0}
                      className="flex-1 text-xs border border-gray-200 rounded-xl px-2.5 py-2 bg-white focus:outline-none focus:border-[#82bbe0] text-gray-700 font-bold disabled:opacity-40"
                    >
                      <option value="">All months</option>
                      {invoiceMonths.map(m => (
                        <option key={m} value={m}>{MONTH_NAMES[m]}</option>
                      ))}
                    </select>

                    {(invoiceFilterYear || invoiceFilterMonth) && (
                      <button
                        onClick={() => { setInvoiceFilterYear(''); setInvoiceFilterMonth(''); }}
                        className="text-xs text-red-500 hover:text-red-700 font-bold px-1.5 py-1.5 rounded transition-all whitespace-nowrap"
                        title="Clear filters"
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>

                  {filteredInvoices.length === 0 ? (
                    <div className="px-6 py-8 text-center">
                      <p className="text-sm text-gray-400 font-medium">
                        {paymentHistory.length === 0 ? 'No invoices yet' : 'No matching invoices'}
                      </p>
                      {paymentHistory.length > 0 && (invoiceFilterYear || invoiceFilterMonth) && (
                        <button
                          onClick={() => { setInvoiceFilterYear(''); setInvoiceFilterMonth(''); }}
                          className="mt-2 text-xs text-[#82bbe0] font-black uppercase tracking-wider hover:underline"
                        >
                          Reset Filters
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                      {filteredInvoices.map(record => (
                        <div key={record.paymentId} className="flex items-center gap-3 px-6 py-4 hover:bg-gray-50 transition-all">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${record.planType === 'subscription' ? 'bg-[#82bbe0]' : 'bg-purple-400'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">{record.planName}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {new Date(record.paidAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              &nbsp;·&nbsp;₹{Number(record.amount).toLocaleString('en-IN')}
                            </p>
                          </div>
                          <button onClick={() => previewReceipt(record)} title="Preview" className="p-2 rounded-xl text-gray-400 hover:text-[#82bbe0] hover:bg-[#82bbe0]/10 transition-all">
                            <Eye size={15} />
                          </button>
                          <button onClick={() => downloadReceipt(record)} title="Download" className="p-2 rounded-xl text-gray-400 hover:text-[#82bbe0] hover:bg-[#82bbe0]/10 transition-all">
                            <Download size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          }
        />
        */}

        {/* Stat cards (Commented Out)
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatCard
            icon={CreditCard}
            label="Current plan"
            value={subscription.planName}
            sub={`Renews ${formatDate(subscription.renewsAt)}`}
          />
          <StatCard
            icon={Users}
            label="Human Agent"
            value={
              userSlotsLoading
                ? '…'
                : userSlotsError
                  ? 'Error'
                  : `${usedUserSlots} / ${maxUserSlots ?? '∞'}`
            }
            sub={
              userSlotsError
                ? userSlotsError
                : freeUserSlots !== null
                  ? `${freeUserSlots} slot${freeUserSlots !== 1 ? 's' : ''} available`
                  : 'Unlimited slots'
            }
            loading={userSlotsLoading}
            color={userSlotsError ? 'text-red-400' : 'text-[#82bbe0]'}
          />
          <StatCard
            icon={Users}
            label="Active agents"
            value={`${activeAgentCount} / ${maxAgents ?? '∞'}`}
            sub={maxAgents ? `${maxAgents - activeAgentCount} slots available` : 'Unlimited slots'}
          />
          <StatCard
            icon={Zap}
            label="Credits remaining"
            value={fmt(creditsRemaining)}
            sub={`of ${fmt(creditsTotal)} this month`}
            color="text-purple-500"
          />
          <StatCard
            icon={Calendar}
            label="Next billing"
            value={
              subscription.planId?.toLowerCase() === 'enterprise'
                ? subscription.planName || 'Enterprise'
                : `₹${fmt(subscription.planPrice)}`
            }
            sub={renewDays !== null ? (renewDays > 0 ? `In ${renewDays} days` : 'Overdue') : ''}
            color={renewDays !== null && renewDays <= 10 ? 'text-amber-500' : 'text-[#82bbe0]'}
            highlight={renewDays !== null && renewDays <= 10}
          />
        </div>
        */}

        {/* Subscription Plans */}
        <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50">
          {/* Subscription Plans Header and billing toggle (Commented Out)
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Subscription Plan</h3>
              <p className="text-[11px] font-medium text-gray-400">Click a plan to pay via Razorpay</p>
            </div>
            <div className="flex items-center gap-1 bg-gray-50 rounded-2xl p-1 border border-gray-100">
              {['monthly', 'yearly'].map(c => (
                <button
                  key={c}
                  onClick={() => setBillingCycle(c)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${billingCycle === c ? 'bg-white shadow-md shadow-gray-100/50 text-gray-900 border border-gray-100' : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  {c === 'monthly' ? 'Monthly' : (
                    <span className="flex items-center gap-1">Yearly <span className="text-green-600 text-[10px] font-bold">-20%</span></span>
                  )}
                </button>
              ))}
            </div>
          </div>
          */}

          {/* Original grid mapping (Commented Out)
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
            {plansList.map((plan, idx) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={planConfig?.id === plan.id}
                isAbove={planConfig && idx > currentPlanIdx}
                billingCycle={billingCycle}
                onOpenModal={setConfirmPlan}
                loading={loadingId === 'plan_' + plan.id}
              />
            ))}
          </div>
          */}

          <div className="flex justify-center">
            <div className="w-full max-w-sm">
              {plansList
                .filter(plan => !planConfig || planConfig.id === plan.id)
                .map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    isCurrent={planConfig?.id === plan.id}
                    billingCycle={billingCycle}
                    onOpenModal={setConfirmPlan}
                    loading={loadingId === 'plan_' + plan.id}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Agents + Credits (Commented Out)
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          // Agent Slots — view only
          <div className={`bg-white rounded-[32px] p-8 border shadow-xl shadow-gray-100/50 ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Agent Slots</h3>
                <p className="text-[11px] font-medium text-gray-400 mt-0.5">
                  {activeAgentCount} of {maxAgents ?? '∞'} slots used
                </p>
              </div>
              {maxAgents && activeAgentCount < maxAgents && serviceActive && (
                <button
                  onClick={handleAddAgent}
                  className="flex items-center gap-1 text-xs font-bold text-[#82bbe0] hover:underline"
                >
                  <Plus size={13} /> Add slot
                </button>
              )}
            </div>
            {maxAgents > 0 && (
              <div className="mb-5 mt-3">
                <ProgressBar used={activeAgentCount} total={maxAgents} />
              </div>
            )}
            <div className="space-y-3 mt-4">
              {agents.map((agent, i) => (
                <AgentSlot
                  key={i}
                  agent={agent}
                  index={i}
                  onAdd={handleAddAgent}
                  disabled={!serviceActive}
                />
              ))}
            </div>
            {maxAgents && activeAgentCount >= maxAgents && serviceActive && (
              <p className="text-xs text-amber-600 mt-5 font-bold">
                All slots used.{' '}
                <button onClick={() => setConfirmPlan(PLAN_CONFIGS['enterprise'])} className="underline font-black">
                  Upgrade your plan
                </button>{' '}
                to add more agents.
              </p>
            )}
          </div>

          // Credits
          <div className={`bg-white rounded-[32px] p-8 border shadow-xl shadow-gray-100/50 space-y-6 ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Credits</h3>
              </div>
              <button
                disabled={!!loadingId}
                onClick={() => onCreditPay(selectedPack)}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-black uppercase tracking-wider text-white bg-[#82bbe0] hover:bg-[#6aabce] rounded-xl transition-all disabled:opacity-60 shadow-md shadow-[#82bbe0]/25 active:scale-[0.98]"
              >
                {loadingId === 'topup_' + selectedPack.k ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                Top up {selectedPack.k}
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-bold text-gray-500 uppercase tracking-wider">
                <span>Monthly included credits</span>
                <span className="text-gray-900">{fmt(creditsUsed)} / {fmt(creditsTotal)}</span>
              </div>
              <ProgressBar used={creditsUsed} total={creditsTotal} />
              <p className="text-[10px] text-gray-400 mt-1 font-medium">
                Resets {formatDate(subscription.renewsAt)} · {fmt(creditsRemaining)} remaining
              </p>
            </div>
            <div className="border-t border-gray-50 my-1"></div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs font-bold text-gray-500 uppercase tracking-wider">
                <span>Top-up credits</span>
                <span className="text-gray-900">{fmt(topupCredits)} remaining</span>
              </div>
              <p className="text-[10px] text-gray-400 font-medium">Never expire · Used after monthly credits</p>
            </div>
            <div className="border-t border-gray-50 my-1"></div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-3">Top-up packs</p>
              <div className="grid grid-cols-3 gap-3">
                {TOPUP_PACKS.map(pack => (
                  <button
                    key={pack.k}
                    disabled={!!loadingId}
                    onClick={() => setSelectedPack(pack)}
                    className={`flex flex-col items-center py-3 px-3 rounded-2xl border-2 transition-all duration-300 text-xs font-bold disabled:opacity-60 cursor-pointer active:scale-[0.98]
                      ${selectedPack.k === pack.k
                        ? 'border-[#82bbe0] bg-[#82bbe0]/10 text-[#82bbe0] ring-2 ring-[#82bbe0]/20'
                        : pack.popular
                          ? 'border-[#82bbe0]/30 bg-[#82bbe0]/5 text-[#82bbe0] hover:bg-[#82bbe0]/10'
                          : 'border-gray-100 text-gray-500 bg-gray-50/30 hover:border-[#82bbe0]/50 hover:bg-white hover:shadow-md hover:shadow-gray-100/50'
                      }`}
                  >
                    {pack.popular && <span className="text-[8px] text-[#82bbe0] font-black uppercase tracking-wider mb-1">Popular</span>}
                    <span className="text-sm font-black text-gray-900">{pack.k}</span>
                    <span className="text-gray-400 font-medium mt-0.5">₹{fmt(pack.price)}</span>
                    {loadingId === 'topup_' + pack.k && <Loader2 size={10} className="animate-spin mt-1 text-[#82bbe0]" />}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div>
                <p className="text-sm font-bold text-gray-800">Auto top-up</p>
                <p className="text-xs text-gray-400 font-medium mt-0.5">Buy 25K credits when balance drops below 10K</p>
              </div>
              <button
                onClick={async () => {
                  const next = !autoTopup;
                  setAutoTopup(next);
                  try {
                    const userStr = sessionStorage.getItem('user');
                    const userObj = userStr ? JSON.parse(userStr) : null;
                    const tenantId = userObj?.tenant_id || userName;
                    await fetch(`${SERVER_URL}/api/credits/auto-topup`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ tenantId, enabled: next }),
                    });
                  } catch {}
                }}
                className={`relative w-12 h-7 rounded-full transition-colors duration-300 focus:outline-none ${autoTopup ? 'bg-[#82bbe0]' : 'bg-gray-200'}`}
              >
                <span className={`absolute top-[3px] left-[3px] w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${autoTopup ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>
        </div>
        */}

        {/* User Slots panel — view only (Commented Out)
        <div className={`bg-white rounded-[32px] p-8 border shadow-xl shadow-gray-100/50 ${!serviceActive ? 'opacity-50 pointer-events-none select-none' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Human Agent</h3>
              <p className="text-[11px] font-medium text-gray-400 mt-0.5">
                {userSlotsLoading
                  ? 'Loading…'
                  : `${usedUserSlots} of ${maxUserSlots ?? '∞'} slots used`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchUserSlots}
                disabled={userSlotsLoading}
                className="p-2 rounded-xl text-gray-400 hover:text-[#82bbe0] hover:bg-[#82bbe0]/10 transition-all disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw size={14} className={userSlotsLoading ? 'animate-spin' : ''} />
              </button>
              {(maxUserSlots === null || usedUserSlots < maxUserSlots) && serviceActive && (
                <button
                  onClick={() => setShowAddSlotModal(true)}
                  className="flex items-center gap-1 text-xs font-bold text-[#82bbe0] hover:underline"
                >
                  <UserPlus size={13} /> Add user
                </button>
              )}
            </div>
          </div>

          {maxUserSlots > 0 && (
            <div className="mb-5 mt-3">
              <ProgressBar used={usedUserSlots} total={maxUserSlots} />
            </div>
          )}

          {userSlotsError && !userSlotsLoading && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3">
              <AlertTriangle size={14} />
              <span>{userSlotsError}</span>
              <button onClick={fetchUserSlots} className="ml-auto underline font-semibold">Retry</button>
            </div>
          )}

          {userSlotsLoading && (
            <div className="space-y-3 mt-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3 py-3 px-4 bg-gray-50/50 rounded-xl border border-gray-100 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-gray-200 rounded w-32" />
                    <div className="h-2.5 bg-gray-100 rounded w-48" />
                  </div>
                  <div className="h-5 w-16 bg-gray-200 rounded-full" />
                </div>
              ))}
            </div>
          )}

          {!userSlotsLoading && (
            <div className="space-y-3 mt-4">
              {userSlots.length === 0 && !userSlotsError && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
                  <Users size={32} className="text-gray-200" />
                  <p className="text-sm font-medium">No user slots assigned yet</p>
                  <button
                    onClick={() => setShowAddSlotModal(true)}
                    className="text-xs text-[#82bbe0] font-bold hover:underline mt-1 uppercase tracking-wider"
                  >
                    + Add first user
                  </button>
                </div>
              )}
              {userSlots.map(slot => (
                <UserSlotRow key={slot.id} slot={slot} />
              ))}
            </div>
          )}

          {!userSlotsLoading && maxUserSlots && usedUserSlots >= maxUserSlots && serviceActive && (
            <p className="text-xs text-amber-600 mt-5 font-bold">
              All user slots used.{' '}
              <button
                onClick={() => setConfirmPlan(PLAN_CONFIGS['enterprise'])}
                className="underline font-black"
              >
                Upgrade your plan
              </button>{' '}
              for more seats.
            </p>
          )}
        </div>
        */}

        {/* Suspended overlay (Commented Out)
        {!serviceActive && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex flex-col items-center text-center gap-3">
            <ShieldOff size={24} className="text-red-400" />
            <div>
              <p className="text-sm font-bold text-red-700">All services are suspended</p>
              <p className="text-xs text-red-500 mt-1">Agent slots and credits are frozen. Renew your subscription to restore access.</p>
            </div>
            <button
              disabled={!!loadingId}
              onClick={() => setConfirmPlan(planConfig || PLAN_CONFIGS['professional'])}
              className="px-5 py-2 bg-red-500 text-white text-sm font-bold rounded-xl hover:bg-red-600 transition-all flex items-center gap-2 disabled:opacity-60"
            >
              {loadingId === 'plan_' + (planConfig?.id || 'professional')
                ? <Loader2 size={14} className="animate-spin" />
                : <RefreshCw size={14} />}
              Renew Subscription
            </button>
          </div>
        )}
        */}

      </div>
    </div>
  );
}
